import {
  ApolloClient, HttpLink, InMemoryCache, from, split 
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { WebSocketLink } from "@apollo/client/link/ws";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { fromPromise } from "@apollo/client/link/utils/fromPromise";
import gql from "graphql-tag";
import axios from "axios";
import { store } from "Globals/store";
import { ONE_SECOND } from "Globals/constants/constants";
import { LOGIN_MANUAL_REFRESH_REQUESTED } from "Globals/dataTypes/auth/auth.duck";
import { map, once } from "ramda";
import showdown from "showdown";
import { init as getKC } from "Globals/dataTypes/login/login.saga";

import { OpenAIApi } from "./openAIApi";
// https://www.apollographql.com/docs/react/api/link/apollo-link-error/
// https://www.apollographql.com/docs/react/data/error-handling/#retrying-operations

// todo: put these into a config and inject via env-vars etc build etc etc etc
const apihosts = {
  prod: "mdv3api.app.masterdocs.com.au",
  failover: ["mdv3api.app.failover.masterdocs.com.au"],
  local: "127.0.0.1:3000",
};
const ping = async (api, secure = true) => {
  for (let i = 1; i < 5; i++) {
    try {
      const { data } = await axios.get(`http${secure ? "s" : ""}://${api}/healthz`, { responseType: "json" });
      if (data.status === "ok") {
        return true;
      }
    } catch (e) {
      console.info(`API ${api} is down!`, e);
    }
    await new Promise(res => setTimeout(res, ONE_SECOND * 2 ** i));
  }
  return false;
};

const currentScriptUrl = new URL(document?.currentScript?.src || window.location);

export const chooseapihost = once(async () => {
  const url = currentScriptUrl;

  const localhosts = [
    "localhost", "127.0.0.1", "internal.docker.host"
  ];
  if (localhosts.includes(window.location.hostname)) {
    await ping(apihosts.local, false);
    return [ apihosts.local, false ];
  }
  const { data } = await axios.get(`${url.origin}/api.txt`);
  const apidefs = data?.split?.("\n").map(i => i.trim());
  const apihost = [ ...(apidefs.length ? apidefs : [apihosts.prod]), ...apihosts.failover ];
  for (const host of apihost) {
    const isAvailable = await ping(host, true);
    if (isAvailable) {
      return [ host, true ];
    }
  }
  throw new Error(`No api available! Checked ${apihost.join(", ")}`);
});
/*
// todo: make this async to support failover
const fallback = async getHostFn => {
    const [primaryhost, isSecure] = getHostFn();
    const fallover = window.__FALLOVER__ || apihost.fallover;
    const checkhost = async host => {
        try {
            const response = await fetch(`http${isSecure ? "s" : ""}://${host}/healthz`);
            const { status } = await response.json();
            return status !== "ok";
        } catch(e) {
            console.error(e);
            console.error("Fallover to failover-host initiated");
            return false;
        }
    };
    const isHostUp = await checkhost(primaryhost);
    if (isHostUp) {
        return primaryhost;
    }
    for (const host of fallover) {
        const up = await checkhost(host);
        if(up) {
            return host;
        }
    }
    throw new Error("ALL APIS ARE DOWN OR NOT REACHABLE! NO FALLOVER HOST COULD BE FOUND");
};
*/

const isUsingV3Api = window?.app?.config?.v3MockApiEnabled || process.env.STORYBOOK_IS_STORYBOOK;
const forceLoginInStorybookEnabled = window?.location?.search?.includes("forcelogin");

const waitForNewToken = actionType => {
  store.dispatch({ type: actionType });
  return new Promise(res => {
    const unsubscribe = store.subscribe(() => {
      const { token: newToken, expiry } = store.getState()?.v3?.auth?.user || {};
      const valid = (expiry && expiry > Date.now()) ?? false;
      if (valid) {
        unsubscribe();
        res(newToken);
      }
    });
  });
};

const publicRouteOperations = [
  "loginUser", "startLogin", "redeemCode", "shortenToken"
];

const asyncAuthLink = setContext(async ({ operationName }, { headers }) => {
  try {
    // if we are using the mock api, we don't want to have to constantly log in
    // including a `forcelogin` query param will override this and show the login modal where required
    if (isUsingV3Api && !(forceLoginInStorybookEnabled || window.forcelogin)) return { headers };

    // If we are logging in etc, we don't want to check for tokens, show login modal etc
    if (publicRouteOperations.includes(operationName)) return { headers };

    const kc = await getKC();
    if (operationName === "refreshUserSession") {
      return {
        headers: {
          ...headers,
          authorization: `Bearer ${kc.refreshToken}`,
        },
      };
    }

    if (kc.isTokenExpired(3 * 2 * 10)) {
      kc.updateToken(); // don't await, just refresh
    }
    if (kc.isTokenExpired(3)) {
      // we still don't have a fresh token, wait for a fresh one!
      await kc.updateToken();
    }
    let token = await kc.getMDToken();
    const jwtRegex = /(^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$)/;

    const isValidToken = tkn => tkn && (tkn.startsWith("MDT-") || jwtRegex.test(tkn));
    while (!isValidToken(token)) {
      console.error("Found invalid jwt token!", token);
      await new Promise(res => setTimeout(res, ONE_SECOND));
      token = await kc.getMDToken();
    }
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    };
  } catch (e) {
    console.error("Error in asyncAuthLink: ", e);
  }
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      const msg = `[GRAPHQL ERROR] - ${message}, Path: ${path}`;
      // if (window?.app?.Duchess?.logNetworkError) {
      //   window?.app?.Duchess?.logNetworkError(msg, { operation, message, path, locations }, message);
      //   return;
      // }
      return console.error(msg, locations);
    });
  }
  if (networkError) {
    const errMessage = `Error: Network error (${networkError.statusCode || "404"}: ${networkError?.response || "Response not successful."}). Please refresh and confirm your change has been made.`;
    if (window?.app?.Duchess?.logNetworkError) {
      window?.app?.Duchess?.logNetworkError(errMessage, { networkError }, networkError);
    } else {
      console.error(errMessage);
    }

    // console.error(`[Network error][${networkError.statusCode}]: ${networkError}`);
    // eslint-disable-next-line no-magic-numbers
    if (networkError.statusCode === 401) {
      // Modify the operation context with a new token
      const oldHeaders = operation.getContext().headers || {};
      const waitForNewTokenProm = waitForNewToken(LOGIN_MANUAL_REFRESH_REQUESTED);
      return fromPromise(waitForNewTokenProm).flatMap(token => {
        operation.setContext({
          headers: {
            ...oldHeaders,
            authorization: token,
          },
        });
        // Retry the request, returning the new observable
        return forward(operation);
      });
    }
    // If some other network error, retry once (the error link will not retry more than once)
    return forward(operation);
  }
});

export const getClientRaw = async () => {
  const [ host, isSecure ] = await chooseapihost();
  const wsLink =
        isUsingV3Api &&
        new WebSocketLink({
          uri: `ws${isSecure ? "s" : ""}://${host}/graphql`,
          options: {
            reconnect: true,
          },
        });

  const httpLink = new HttpLink({
    uri: `http${isSecure ? "s" : ""}://${host}/graphql`,
    credentials: "include",
  });

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation !== "subscription";
    },
    httpLink,
    wsLink
  );

  return new ApolloClient({
    link: from([
      errorLink, asyncAuthLink, splitLink
    ]),
    cache: new InMemoryCache(),
  });
};

export const getClient = function getClient(optCacheKey) {
  if (optCacheKey) {
    if (getClient[optCacheKey]) {
      return getClient[optCacheKey];
    }
    getClient[optCacheKey] = getClientRaw();
    return getClient[optCacheKey];
  }
  return getClientRaw();
};

export const makeGqlQuery = async ({ query, ...rest }) => {
  const client = await getClient();
  const data = await client.query({
    query: gql`
            ${query}
        `,
    fetchPolicy: "no-cache",
    ...rest,
  });
  return data;
};

export const openAIApi = new OpenAIApi();

// expose client on window for use in v2
window.gqlQuery = makeGqlQuery;
window.openAIApi = openAIApi;
window.converter = new showdown.Converter();